import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, doc, limit, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";

import { db } from "../lib/firebase";
import { useSession, useCan } from "../lib/auth";
import { useCollection } from "../hooks/useCollection";
import { useCallableAction } from "../hooks/useCallableAction";
import { PageHeader, EmptyState, Spinner, ErrorNote } from "../components";
import { since } from "../lib/format";

/**
 * BİLDİRİMLER — eski `notifications.php` karşılığı.
 *
 * DÜZELTİLEN ESKİ HATALAR
 *  · Bildirim hangi kayda ait olduğunu taşımıyordu; tıklayıp gitmek imkânsızdı.
 *    Artık `refType`/`refId` var ve satır tıklanabilir.
 *  · Sorgu ZORUNLU olarak `userId == uid` filtresi taşır — Security Rules
 *    filtresiz liste sorgusunu reddeder (kural testiyle kanıtlı).
 *
 * ⚠ KALDIRILAN İDDİA: burada "expiresAt gerçek bir Firestore TTL alanıdır,
 *   14 gün sonra kayıt kendiliğinden silinir" YAZIYORDU. Bu doğru değildi.
 *   Firestore'da silme işini alanın varlığı değil, alana bağlanmış bir TTL
 *   POLİTİKASI yapar. `demontha-balder` projesi Firestore Admin API'ye
 *   sorulduğunda `ttlConfig` tanımlı tek bir alan bile dönmüyor — yani bugün
 *   hiçbir bildirim silinmiyor, tablo eskisi gibi büyümeye devam ediyor.
 *   Politika kurulana kadar (bkz. `shared/notify.ts` içindeki gcloud komutu)
 *   burada silinme sözü VERİLMEZ.
 */

interface NotificationDoc {
  id: string;
  userId: string;
  type?: string;
  message: string;
  refType?: string;
  refId?: string;
  isRead?: boolean;
  createdAt?: unknown;
}

const REF_ROUTE: Record<string, (id: string) => string> = {
  order: (id) => `/orders/${id}`,
  production: (id) => `/production/${id}`,
  ssh: () => `/after-sales`,
  stockOrder: () => `/stock-orders`,
  cuttingList: () => `/cutting-list`,
};

/*
 * ★ OPERASYON UYARILARI (`opsAlerts`)
 *
 * TESPİT (canlı ölçüm): koleksiyonda 51 uyarı var, HİÇBİRİ çözülmüş değil.
 * Ekran her uyarının altına `context` alanını `JSON.stringify(…, null, 2)` ile
 * OLDUĞU GİBİ basıyordu. Sonuç telefonda okunamayan bir duvar:
 *   {"shortages":[{"missing":2,"name":"SOMUN AÇIK","hasInventoryRecord":false,
 *    "componentId":"somun-acik-adet","inventoryKey":"part_somun-acik-adet",…}]}
 * Üstelik `uid`, `role`, `inventoryKey`, `productionId` gibi İÇ KİMLİKLER
 * ekranda görünüyordu — okunabilirlik kadar gizlilik sorunu da.
 *
 * Yeni düzen: kod başına GRUP + insan diliyle özet + "ne yapmalı" satırı.
 * Ham gövde yalnızca "Ayrıntı" ile açılır, varsayılan KAPALI ve kimlikler
 * temizlenmiş hâlde.
 *
 * ⚠ Aşağıdaki iki sözlük `functions/src/shared/notify.ts` ile BİREBİR aynı
 * olmak zorundadır. `functions` ve `web` ayrı paketler olduğu için import
 * edilemiyor; ayrışma `functions/test/unit/opsAlertCodes.test.ts` ve
 * `rulesContract.test.ts` tarafından metin karşılaştırmasıyla denetlenir.
 */
const OPS_ALERT_LABELS: Record<string, string> = {
  "backup-failed": "Günlük yedek alınamadı",
  "consumption-warnings": "Stok düşümünde atlanan kalemler var",
  "gdpr-request": "KVKK/GDPR veri talebi geldi",
  "integrity-check": "Veri bütünlüğü taraması bulgu buldu",
  "klaviyo-event-failed": "Müşteriye üretim aşaması maili gönderilemedi",
  "order-cancelled-in-production": "Üretimdeki sipariş iptal edildi",
  "order-edited-in-production": "Üretimdeki sipariş sonradan değiştirildi",
  "price-drift-detected": "Satış fiyatları Shopify ile uyuşmuyordu",
  "production-open-failed": "Üretim kaydı açılamadı",
  "recipe-missing": "Ürünün reçetesi tanımlı değil",
  "shipment-shopify-sync-failed": "Kargo takip bilgisi Shopify'a gönderilemedi",
  "shipment-status-move-failed": "Takip no girildi ama sipariş kargoya alınamadı",
  "shipment-stock-deduct-failed": "Sevkiyatta stok düşülemedi",
  "shopify-tag-sync-failed": "Shopify sipariş etiketi güncellenemedi",
  "shopify-uninstalled": "Shopify uygulaması kaldırıldı — sipariş akışı durdu",
  "shopify-webhook-dead": "Shopify bildirimi işlenemedi",
  "stock-negative": "Üretim yapıldı ama stok eksiye düştü",
  "stock-record-missing": "Bileşenlerin stok kartı açılmamış",
  "stock-shortage": "Hammadde yetersiz — üretim tamamlanamadı",
  "stockorder-quantity-mismatch": "Stok siparişinde gelen adet tutmuyor",
  "trendyol-non-stock-product": "Trendyol'da stok takibi olmayan ürün satıldı",
  "trendyol-scan-truncated": "Trendyol sipariş taraması yarıda kesildi",
  "trendyol-unknown-sku": "Trendyol'da tanınmayan SKU satıldı",
  "unknown-status": "Sipariş tanınmayan bir statüde geldi",
  "user-claims-desync": "Kullanıcının yetkisi ile kaydı birbirini tutmuyor",
  "webhook-dead-letters": "İşlenemeyen Shopify bildirimleri birikti",
  "ups-track-failed": "UPS kargo takibi sorgulanamıyor",
  "webhook-hmac-failed": "Shopify webhook imzası doğrulanamıyor",
  "webhook-miss": "Yedek tarama, kaçmış sipariş yakaladı",
  "webhook-retry-exhausted": "Shopify bildirimi ikinci denemede de işlenemedi",
  "webhook-subscription-misrouted": "Shopify bildirimi yanlış adrese gidiyor",
  "webhook-subscription-missing": "Shopify bildirim aboneliği düşmüş",
};

const OPS_ALERT_ACTIONS: Record<string, string> = {
  "backup-failed": "Günlük yedek alınamadı",
  "consumption-warnings": "Atlanan kalemlerin reçetesini tamamlayıp stok sayımını elle düzeltin.",
  "gdpr-request": "Talebi 30 gün içinde yanıtlayın; ilgili müşteri verisini silin veya dışa aktarın.",
  "integrity-check": "Aşağıdaki bulguları ilgili ekranlardan düzeltin (reçetesi/ambalajı olmayan ürünler, negatif stoklar).",
  "klaviyo-event-failed":
    'Uyarıdaki hata "KLAVIYO_API_KEY tanımlı değil" ya da HTTP 401 ise anahtar eksik/yanlıştır: ' +
    "`firebase functions:secrets:set KLAVIYO_API_KEY` ile yazıp fonksiyonları yeniden dağıtın. " +
    "Başka bir hata ise bildirim bir sonraki aşama değişikliğinde kendiliğinden yeniden denenir; " +
    "üretim ve kargo akışı bundan etkilenmez. Bildirimleri tümden durdurmak için " +
    "Ayarlar → Müşteri Bildirimleri anahtarını kapatın.",
  "order-cancelled-in-production": "Üretimi durdurun; hazırlanmış parça varsa stoğa geri alın.",
  "order-edited-in-production": "Üretim kartını yeni sipariş içeriğiyle karşılaştırıp kesim listesini yenileyin.",
  "price-drift-detected":
    "Fiyatlar düzeltildi; ayrıca Shopify uygulama bildirimlerini (products/update) kontrol edin — bu kadar sapma webhook'un çalışmadığına işaret eder.",
  "production-open-failed": "Siparişi Siparişler ekranından elle üretime gönderin.",
  "recipe-missing": "Ürün kartının Reçete sekmesinden bileşenleri tanımlayın; aksi hâlde stok hiç düşmez.",
  // ★ Ekran adı DÜZELTİLDİ: düğme Kargo ekranında değil, sipariş detayının
  // Kargo kutusunda. Kargo ekranındaki kırmızı "Shopify'a gitmedi" rozeti de
  // zaten oraya götürür. (Aynı metin `functions/src/shared/notify.ts` içinde.)
  "shipment-shopify-sync-failed":
    "Siparişi açın, Kargo kutusundaki \"Shopify'a yeniden gönder\" düğmesiyle tekrar deneyin. Düğme hata veriyorsa Shopify uygulama izinlerini kontrol edin.",
  "shipment-status-move-failed": "Siparişi Siparişler ekranından elle \"Kargoya Verildi\"ye alın; takip numarası kayıtlı, sipariş yanlış sekmede duruyor.",
  "shipment-stock-deduct-failed": "Stoklar ekranından ilgili bileşenleri elle düşün.",
  "shopify-tag-sync-failed": "Shopify uygulama izinlerini kontrol edin; etiket eksik kalırsa sipariş filtreleri yanılır.",
  "shopify-uninstalled": "Shopify uygulamasını yeniden kurun — kurulana kadar hiçbir sipariş düşmez.",
  "shopify-webhook-dead": "Yedek tarama siparişi yakalar; yine de siparişin geldiğini Siparişler ekranından doğrulayın.",
  "stock-negative": "Stoklar ekranından ilgili bileşenlerin sayımını girin; stok gerçekte eksi olamaz.",
  "stock-record-missing": "Stoklar ekranından bu bileşenlere stok kartı açıp sayım girin.",
  "stock-shortage": "Eksik bileşenler için satın alma açın veya stok sayımını güncelleyin.",
  "stockorder-quantity-mismatch": "Stok siparişi kartından gelen adedi irsaliyeyle karşılaştırıp düzeltin.",
  "trendyol-non-stock-product": "Ürünün stok takibini açın veya bilerek takipsizse uyarıyı çözüldü işaretleyin.",
  "trendyol-scan-truncated": "Taramayı elle yeniden çalıştırın; kalan siparişler alınmamış olabilir.",
  "trendyol-unknown-sku": "SKU'yu ürün kartıyla eşleştirin; eşleşmeyen sipariş üretime düşmez.",
  "unknown-status": "Statüyü Siparişler ekranından geçerli bir statüye çekin.",
  "user-claims-desync":
    "Kullanıcılar ekranından kaydı açıp kaydedin; yetkiler yeniden yazılır. " +
    "Uyarı \"öksüz kayıt\" diyorsa tekrar denemek düzeltmez: o uid için Firebase Auth " +
    "hesabı yok, kaydı silin veya kullanıcıyı yeniden davet edin.",
  "webhook-dead-letters": "Shopify sağlık ekranından yeniden denemeyi çalıştırın.",
  "ups-track-failed":
    "UPS erişim bilgilerini ve kotayı kontrol edin; takip durumu güncellenmiyor.",
  "webhook-hmac-failed": "Shopify webhook imzası doğrulanamıyor",
  "webhook-miss": "Bilgi amaçlıdır, işlem gerekmez; sık tekrarlıyorsa webhook aboneliklerini kontrol edin.",
  "webhook-retry-exhausted": "İlgili siparişi Shopify panelinden bulup ERP'de elle oluşturun.",
  "webhook-subscription-misrouted": "Shopify uygulama adresini güncelleyin; bildirimler ERP'ye ulaşmıyor.",
  "webhook-subscription-missing": "Sistem aboneliği kendi kuruyor; bir sonraki sağlık taramasında tekrarlarsa izinleri kontrol edin.",
};

/**
 * ★ EKRANDA ASLA GÖSTERİLMEYEN ALANLAR.
 *
 * Bunlar operatöre hiçbir şey anlatmayan iç kimlikler; "Ayrıntı" açılsa bile
 * temizlenir. `uid`/`role` kullanıcı mahremiyeti, `inventoryKey`/`componentId`
 * ise Firestore doküman anahtarı — ikisi de ekranda işi olmayan veriler.
 */
const REDACTED_KEYS = new Set([
  "uid",
  "role",
  "detectedBy",
  "resolvedBy",
  "componentId",
  "inventoryKey",
]);

interface OpsAlertDoc {
  id: string;
  code?: string;
  message?: string;
  context?: Record<string, unknown>;
  resolved?: boolean;
  createdAt?: unknown;
}

/** `context` içinden iç kimlikleri ÖZYİNELEMELİ olarak siler. */
function redact(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (REDACTED_KEYS.has(k)) continue;
      out[k] = redact(v);
    }
    return out;
  }
  return value;
}

/** "A, B, C — 9 bileşen" biçimi. Uzun listeyi ilk üçle temsil eder. */
function nameList(names: string[], unit: string): string {
  const shown = names.slice(0, 3).join(", ");
  return names.length > 3 ? `${shown} — ${names.length} ${unit}` : shown;
}

function asRecordArray(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v) ? (v.filter((x) => x && typeof x === "object") as Record<string, unknown>[]) : [];
}

/**
 * Koda göre TEK SATIRLIK insan-okunur özet.
 *
 * Amaç: ham JSON'un yerini almak. Bir kodu tanımıyorsak uydurma yapmayız —
 * genel bir sayaç özeti döneriz, ayrıntı yine "Ayrıntı" altında durur.
 */
function summarize(code: string, ctx: Record<string, unknown> | undefined): string {
  if (!ctx || Object.keys(ctx).length === 0) return "";

  switch (code) {
    case "stock-record-missing":
    case "stock-shortage":
    case "stock-negative": {
      const rows = asRecordArray(ctx.shortages);
      if (rows.length === 0) return "";
      const names = rows.map((r) => String(r.name ?? "?"));
      return nameList(names, "bileşen");
    }

    case "integrity-check": {
      const parts: string[] = [];
      const push = (key: string, label: string) => {
        const n = Array.isArray(ctx[key]) ? (ctx[key] as unknown[]).length : 0;
        if (n > 0) parts.push(`${n} ${label}`);
      };
      push("productsWithoutRecipe", "ürünün reçetesi yok");
      push("productsWithoutPackaging", "ürünün ambalajı yok");
      push("orphanRecipeLines", "sahipsiz reçete satırı");
      push("negativeStock", "bileşende negatif stok");
      push("mirrorFixed", "kayıt onarıldı");
      push("counterFixed", "sayaç düzeltildi");
      return parts.join(" · ");
    }

    case "klaviyo-event-failed": {
      const parts: string[] = [];
      if (ctx.orderId) parts.push(`Sipariş ${String(ctx.orderId)}`);
      if (ctx.metricName) parts.push(`Mesaj: ${String(ctx.metricName)}`);
      if (ctx.stageId) parts.push(`Aşama: ${String(ctx.stageId)}`);
      return parts.join(" · ");
    }

    case "shipment-shopify-sync-failed": {
      const parts: string[] = [];
      if (ctx.orderId) parts.push(`Sipariş ${String(ctx.orderId)}`);
      if (ctx.trackingNumber) parts.push(`Takip ${String(ctx.trackingNumber)}`);
      if (ctx.error) parts.push(String(ctx.error));
      return parts.join(" · ");
    }

    case "shipment-status-move-failed": {
      const parts: string[] = [];
      if (ctx.orderId) parts.push(`Sipariş ${String(ctx.orderId)}`);
      if (ctx.from) parts.push(`Kaldığı statü: ${String(ctx.from)}`);
      return parts.join(" · ");
    }

    case "shopify-tag-sync-failed":
      return ctx.orderId
        ? `Sipariş ${String(ctx.orderId)}${ctx.status ? ` (${String(ctx.status)})` : ""}`
        : "";

    case "webhook-subscription-missing":
    case "webhook-subscription-misrouted": {
      const missing = Array.isArray(ctx.missing) ? ctx.missing.map(String) : [];
      return missing.length > 0 ? nameList(missing, "abonelik") : "";
    }

    case "webhook-miss": {
      const parts: string[] = [];
      if (typeof ctx.scanned === "number") parts.push(`${ctx.scanned} sipariş tarandı`);
      if (typeof ctx.approved === "number" && ctx.approved > 0) parts.push(`${ctx.approved} onaylandı`);
      if (typeof ctx.repaired === "number" && ctx.repaired > 0) parts.push(`${ctx.repaired} onarıldı`);
      if (typeof ctx.productionCreated === "number" && ctx.productionCreated > 0) {
        parts.push(`${ctx.productionCreated} üretim açıldı`);
      }
      return parts.join(", ");
    }

    // `uid` DIŞINDA hiçbir şey taşımıyor; kimliği göstermeyeceğimize göre
    // söylenecek tek doğru şey kaç kullanıcıyı ilgilendirdiği.
    case "user-claims-desync":
      return "1 kullanıcı etkilendi";

    case "consumption-warnings":
    case "recipe-missing": {
      const warnings = Array.isArray(ctx.warnings) ? ctx.warnings.map(String) : [];
      return warnings.length > 0 ? nameList(warnings, "uyarı") : "";
    }

    default: {
      // Bilinmeyen kod: skaler alanları "anahtar: değer" olarak göster,
      // dizileri say. Ham JSON'dan her hâlükârda okunabilir.
      const parts: string[] = [];
      for (const [k, v] of Object.entries(redact(ctx) as Record<string, unknown>)) {
        if (Array.isArray(v)) parts.push(`${k}: ${v.length} kayıt`);
        else if (v !== null && typeof v === "object") continue;
        else if (v !== "" && v !== null && v !== undefined) parts.push(`${k}: ${String(v)}`);
        if (parts.length >= 4) break;
      }
      return parts.join(" · ");
    }
  }
}

/** Tek uyarı satırı — özet üstte, ham gövde "Ayrıntı" altında ve KAPALI. */
function AlertRow({
  alert,
  onResolve,
  busy,
}: {
  alert: OpsAlertDoc;
  onResolve: (id: string) => void;
  busy: boolean;
}) {
  const code = alert.code ?? "";
  const summary = summarize(code, alert.context);
  const safeContext = alert.context ? (redact(alert.context) as Record<string, unknown>) : {};
  const hasDetail = Object.keys(safeContext).length > 0;

  return (
    <li className="px-3 py-3 sm:px-4">
      {/* min-w-0: uzun içeriğin kabı taşırmasını engelleyen anahtar sınıf. */}
      <div className="min-w-0">
        <p className="break-words text-sm text-gray-800">{alert.message}</p>

        {summary ? (
          <p className="mt-1 break-words text-sm font-medium text-gray-900">{summary}</p>
        ) : null}

        <p className="mt-1 text-xs text-gray-500">{since(alert.createdAt)}</p>

        {hasDetail ? (
          <details className="mt-2 group">
            <summary className="cursor-pointer list-none text-xs font-medium text-primary-700 underline underline-offset-2">
              Ayrıntı
            </summary>
            {/*
              Ham gövde YALNIZCA burada ve kimlikleri temizlenmiş hâlde.
              overflow-auto + max-h: uzun JSON sayfayı değil KENDİ kabını kaydırır
              (telefonda yatay taşma ekranı bozuyordu).
            */}
            <pre className="mt-1 max-h-64 overflow-auto rounded bg-gray-50 p-2 text-xs leading-relaxed text-gray-700">
              {JSON.stringify(safeContext, null, 2)}
            </pre>
          </details>
        ) : null}

        <button
          type="button"
          disabled={busy}
          onClick={() => onResolve(alert.id)}
          className="mt-2 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {busy ? "İşleniyor…" : "Çözüldü işaretle"}
        </button>
      </div>
    </li>
  );
}

/** Yalnızca `boss` görür (Rules: `match /opsAlerts` → `allow get, list: if isBoss()`). */
function OpsAlertPanel() {
  const { data, loading, error } = useCollection<OpsAlertDoc>(
    () => query(collection(db, "opsAlerts"), orderBy("createdAt", "desc"), limit(200)),
    [],
  );

  const resolveAction = useCallableAction<
    { alertId?: string; code?: string },
    { resolvedCount: number; code: string; message: string }
  >("resolveOpsAlert");

  const [openCode, setOpenCode] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const open = useMemo(() => data.filter((a) => a.resolved !== true), [data]);

  /**
   * Koda göre GRUPLAMA — telefonda okunabilirliğin asıl kaynağı.
   * Canlıda 51 uyarı yalnızca 9 koddan geliyor; 51 satır yerine 9 başlık.
   */
  const groups = useMemo(() => {
    const m = new Map<string, OpsAlertDoc[]>();
    for (const a of open) {
      const c = a.code ?? "";
      const list = m.get(c);
      if (list) list.push(a);
      else m.set(c, [a]);
    }
    return [...m.entries()].sort((x, y) => y[1].length - x[1].length);
  }, [open]);

  async function resolveOne(alertId: string) {
    setBusyId(alertId);
    setDone(null);
    const r = await resolveAction.run({ alertId });
    if (r) setDone(r.message);
    setBusyId(null);
  }

  async function resolveCode(code: string, count: number) {
    const label = OPS_ALERT_LABELS[code] ?? code;
    // Toplu ve geri alınamaz yazma: onay olmadan çalışmaz.
    if (!window.confirm(`"${label}" kodundaki ${count} uyarının TAMAMI çözüldü işaretlenecek. Onaylıyor musunuz?`)) {
      return;
    }
    setBusyId(code);
    setDone(null);
    const r = await resolveAction.run({ code });
    if (r) setDone(r.message);
    setBusyId(null);
  }

  if (loading) return <Spinner />;

  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-semibold text-gray-900">
        Operasyon uyarıları{open.length > 0 ? ` (${open.length} çözülmemiş)` : ""}
      </h2>

      <ErrorNote error={error} className="mb-2" />
      <ErrorNote error={resolveAction.error} className="mb-2" />

      {done ? (
        <p role="status" className="mb-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
          {done}
        </p>
      ) : null}

      {groups.length === 0 ? (
        <p className="rounded-lg bg-white px-4 py-3 text-sm text-gray-600 ring-1 ring-gray-200">
          Çözülmemiş operasyon uyarısı yok.
        </p>
      ) : (
        <ul className="space-y-2">
          {groups.map(([code, items]) => {
            const label = OPS_ALERT_LABELS[code] ?? code;
            const action = OPS_ALERT_ACTIONS[code] ?? "";
            const isOpen = openCode === code;

            return (
              <li key={code} className="overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
                <div className="flex items-start gap-3 px-3 py-3 sm:px-4">
                  <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />

                  <div className="min-w-0 flex-1">
                    {/* BAŞLIK: tek cümle, ham kod değil. */}
                    <p className="break-words text-sm font-semibold text-gray-900">
                      {label}
                      <span className="ml-1.5 font-normal text-gray-500">({items.length})</span>
                    </p>

                    {/* NE YAPMALI: tek satır, eyleme dönük. */}
                    {action ? (
                      <p className="mt-1 break-words text-sm text-gray-700">{action}</p>
                    ) : null}

                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setOpenCode(isOpen ? null : code)}
                        aria-expanded={isOpen}
                        className="rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {isOpen ? "Gizle" : `${items.length} kaydı göster`}
                      </button>

                      <button
                        type="button"
                        disabled={busyId === code}
                        onClick={() => void resolveCode(code, items.length)}
                        className="rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {busyId === code ? "İşleniyor…" : `Tümünü çözüldü işaretle (${items.length})`}
                      </button>
                    </div>

                    {/* Ham kod en altta, küçük ve soluk: gerektiğinde aranabilsin. */}
                    <p className="mt-1.5 break-all font-mono text-xs text-gray-500">{code}</p>
                  </div>
                </div>

                {isOpen ? (
                  <ul className="divide-y divide-gray-100 border-t border-gray-200 bg-gray-50/50">
                    {items.map((a) => (
                      <AlertRow key={a.id} alert={a} onResolve={(id) => void resolveOne(id)} busy={busyId === a.id} />
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function Notifications() {
  const session = useSession();
  const can = useCan();
  const uid = session?.user.uid ?? "";
  const [onlyUnread, setOnlyUnread] = useState(false);

  const { data, loading, error } = useCollection<NotificationDoc>(
    () =>
      uid
        ? query(
            collection(db, "notifications"),
            where("userId", "==", uid),
            orderBy("createdAt", "desc"),
            limit(200),
          )
        : null,
    [uid],
  );

  const rows = useMemo(
    () => (onlyUnread ? data.filter((n) => n.isRead !== true) : data),
    [data, onlyUnread],
  );

  /*
   * Okunmamış sayısı BURADAN hesaplanır — `users.unreadNotificationCount`
   * alanından DEĞİL. O alan 5 yerde yazılıp 0 yerde okunuyordu ve azaltan kod
   * yoktu; `firestore.rules` içinde `users` dokümanı `update: if false` olduğu
   * için istemcinin azaltması da imkânsızdı. Alan artık yazılmıyor
   * (bkz. `functions/src/shared/notify.ts`), bu satır ise zaten doğruydu.
   */
  const unread = useMemo(() => data.filter((n) => n.isRead !== true).length, [data]);

  async function markRead(n: NotificationDoc) {
    if (n.isRead === true) return;
    // Kural: yalnızca isRead false→true, damga sunucu zamanı olmak zorunda.
    await updateDoc(doc(db, "notifications", n.id), {
      isRead: true,
      readAt: serverTimestamp(),
    });
  }

  async function markAllRead() {
    await Promise.all(rows.filter((n) => n.isRead !== true).map(markRead));
  }

  return (
    <PageHeader
      title="Bildirimler"
      description={unread > 0 ? `${unread} okunmamış bildirim` : "Tüm bildirimler okundu."}
      actions={
        <>
          <button
            type="button"
            onClick={() => setOnlyUnread((v) => !v)}
            className="btn bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            {onlyUnread ? "Tümünü göster" : "Yalnızca okunmamış"}
          </button>
          {unread > 0 ? (
            <button type="button" onClick={() => void markAllRead()} className="btn btn-primary">
              Tümünü okundu işaretle
            </button>
          ) : null}
        </>
      }
    >
      {can.isBoss ? <OpsAlertPanel /> : null}

      <ErrorNote error={error} className="mb-4" />

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Bildirim yok"
          description={onlyUnread ? "Okunmamış bildirim kalmadı." : "Henüz bildirim almadınız."}
        />
      ) : (
        <ul className="divide-y divide-gray-200 rounded-lg bg-white ring-1 ring-gray-200">
          {rows.map((n) => {
            const route = n.refType && n.refId ? REF_ROUTE[n.refType]?.(n.refId) : undefined;
            const body = (
              <div className="flex items-start gap-3 px-3 py-3 sm:px-4">
                <span
                  aria-hidden="true"
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                    n.isRead ? "bg-transparent" : "bg-primary-600"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  {/* break-words: uzun ürün adları telefonda satırı taşırıyordu. */}
                  <p className={`break-words text-sm ${n.isRead ? "text-gray-600" : "font-medium text-gray-900"}`}>
                    {n.message}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-600">{since(n.createdAt)}</p>
                </div>
                {n.isRead ? null : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      void markRead(n);
                    }}
                    className="shrink-0 text-xs text-gray-500 underline hover:text-gray-700"
                  >
                    Okundu
                  </button>
                )}
              </div>
            );

            return (
              <li key={n.id}>
                {route ? (
                  <Link to={route} onClick={() => void markRead(n)} className="block hover:bg-gray-50">
                    {body}
                  </Link>
                ) : (
                  body
                )}
              </li>
            );
          })}
        </ul>
      )}
    </PageHeader>
  );
}

export default Notifications;
